<script setup>
import md5 from "js-md5";
import { ref } from "vue";
import { useStore } from "vuex";
import * as z from "zod";
import ThirdPartyLoginButtons from "./ThirdPartyLoginButtons.vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "switchToSignUp"]);

const toast = useToast();
const store = useStore();
const router = useRouter();

// 使用认证模态框 composable 获取跳转地址
const { consumeRedirect } = useAuthModals();

const getUserInfo = (payload) => store.dispatch("user/getUserInfo", payload);
const loading = ref(false);

const fields = [
  {
    name: "email",
    type: "email",
    label: "Email",
    placeholder: "Enter your email",
    required: true,
  },
  {
    name: "password",
    type: "password",
    label: "Password",
    placeholder: "Enter your password",
    required: true,
    class: "password-input-fix",
    inputClass: "password-input-fix",
  },
];

const schema = z.object({
  email: z.string().email("Invalid email"),
  password: z
    .string("Password is required")
    .min(6, "Must be at least 6 characters"),
});

async function onSubmit({ data }) {
  loading.value = true;
  try {
    const payload = JSON.stringify({
      email_address: data.email,
      password: md5(data.password),
    });
    const result = await store.dispatch("user/login", payload);
    if (result?.status === 200) {
      await getUserInfo({ user_id: result.data.userinfo.user_info_id });
      localStorage.setItem("login_mode", "default");
      emit("close");
      // 检查是否有登录后跳转地址
      const redirect = consumeRedirect();
      if (redirect) {
        router.push(redirect);
      }
    } else if (
      result?.status === 410 &&
      result?.message === "InvalidEmailAddress"
    ) {
      toast.add({
        title: "Error",
        icon: "i-lucide-alert-triangle",
        description: "Email address is invalid",
        color: "error",
      });
    } else if (
      result?.status === 412 &&
      result?.message === "InvalidPassword"
    ) {
      toast.add({
        title: "Error",
        icon: "i-lucide-alert-triangle",
        description: "Password is incorrect",
        color: "error",
      });
    } else {
      toast.add({
        title: "Error",
        icon: "i-lucide-alert-triangle",
        description: result?.message || "Sign in failed",
        color: "error",
      });
    }
  } catch (err) {
    console.error(err);
    toast.add({
      title: "Error",
      icon: "i-lucide-alert-triangle",
      description: "Sign in failed",
      color: "error",
    });
  } finally {
    loading.value = false;
  }
}

function handleThirdPartySuccess() {
  emit("close");
  // 检查是否有登录后跳转地址
  const redirect = consumeRedirect();
  if (redirect) {
    router.push(redirect);
  }
}
</script>

<template>
  <UModal
    v-model:open="props.isOpen"
    @update:open="emit('close')"
    :dismissible="false"
    :ui="{
      content:
        'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-md max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] rounded-lg shadow-lg ring ring-default overflow-hidden',
    }"
  >
    <template #default>
      <span class="hidden"></span>
    </template>

    <template #content>
      <UButton
        color="neutral"
        variant="ghost"
        icon="i-lucide-x"
        aria-label="Close modal"
        @click="emit('close')"
        class="absolute top-4 right-4 z-10"
      />
      <div class="p-4 sm:p-6">
        <UAuthForm
          :fields="fields"
          :schema="schema"
          :loading="loading"
          title="Sign in to SharkFoto"
          icon="i-lucide-lock"
          :submit="{ label: 'Sign In', block: true, class: 'text-white' }"
          @submit="onSubmit"
          :ui="{
            input: 'font-[unset]',
            body: 'gap-y-0',
            password: 'font-[unset]',
          }"
        >
          <template #providers>
            <ThirdPartyLoginButtons
              mode="signin"
              @success="handleThirdPartySuccess"
            />
            <USeparator label="or" />
          </template>
          <template #description>
            Don't have an account?
            <UButton
              variant="link"
              class="text-[#8A53FC] hover:underline p-0"
              @click="emit('switchToSignUp')"
            >
              Sign up and get free credits
            </UButton>
          </template>
          <template #password-hint>
            <ULink
              to="/reset-password"
              class="text-primary font-medium"
              tabindex="-1"
            >
              Forgot password?
            </ULink>
          </template>
        </UAuthForm>
      </div>
    </template>
  </UModal>
</template>

<style>
input[type="password"].password-input-fix,
input[type="password"] {
  font-family: Arial, Helvetica, sans-serif;
}
</style>
